import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { sendLinearUpdate } from "~/Configurators/Linear/api/sendLinearConfiguratorUpdate";
import { sendLinearDataReset } from "~/Configurators/Linear/api/sendLinearStepReset";
import { sendLinearMiscChange, sendLinearStepChange } from "~/Configurators/Linear/api/sendLinearStepChange";
import { sendLinearResetConfigurator } from "~/Configurators/Linear/api/sendLinearResetConfigurator";
import { sendLinearAddToCart } from "~/Configurators/Linear/api/sendLinearAddToCart";
import { SummaryData } from "~/Configurators/Linear/v2/configuration/kllt/summary/KLLTUnitSummaryData";
import { type RootState } from "~/redux/store";
import { LinearSummaryParams } from "~/Configurators/Linear/components/LinearSummaryParams";
import { type ConfiguratorPageDefinition, type StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import { KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { CENTRAL_CONNECTORS_VALUE, MANUAL_CONNECTORS_VALUE } from "~/Configurators/Linear/constants";
import * as UnitCstic from "~/Configurators/Linear/v2/configuration/kllt/KLLTUnitCstics";
import * as CarriageCstics from "~/Configurators/Carriage/v2/configuration/kwlt/KWLTCarriageCstics";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { getCsticsValue } from "~/Configurators/common/v2/helper";
import { BASE_DESIGNATION_SEPARATOR } from "~/Configurators/Linear/constants";
import { KmatType } from "~/Configurators/Linear/kmatType";
import type { LinearConfiguration, LinearCstic, LinearSubstep } from "~/Configurators/Linear/models";
import { validateFromDefinition } from "~/Configurators/common/v2/validation";
import { findSelectedTileIndex } from "~/Configurators/Linear/utils";
import {
  makeSendStepCompletedEvent,
  makeSummaryDataProvider,
  transformCarriageDataForGA,
  transformRailDataForGA,
  type LCGAData,
} from "~/Configurators/Shaft/shaftGaHelper";
import type { RailKmatSummaryData, CarriageKmatSummaryData } from "~/Configurators/Shaft/SummaryDataProvider";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep2SummaryDataRail,
  getStep3SummaryDataCarriage,
  prepareSubStepsInfo,
} from "./summary/KLLTUnitSummaryDataProvider";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { toMapFn } from "~/utils/reducers";
import { prepareSummaryDataName, prepareSummaryDataValue } from "~/Configurators/common/v2/gaHelper";
import { selectRailAmount } from "~/Configurators/Linear/redux/selectors";

const UNIT_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.SystemLayout, SubstepType.Size];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      const selectionData = toMapFn(step1Data, prepareSummaryDataName, prepareSummaryDataValue);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(1), selectionData);
    },
  },
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      const selectionData: LCGAData = toMapFn(step2Data, prepareSummaryDataName, prepareSummaryDataValue);

      const guidewayData: RailKmatSummaryData[] = getStep2SummaryDataRail(summaryDataProvider) ?? [];
      selectionData["configured_guideway_data"] = transformRailDataForGA(guidewayData);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(2), selectionData);
    },
  },
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);

      const selectionData: LCGAData = {};

      const railAmount = selectRailAmount(state);

      const carriageData: CarriageKmatSummaryData[] = getStep3SummaryDataCarriage(summaryDataProvider) ?? [];
      selectionData["configured_carriage_data"] = transformCarriageDataForGA(carriageData, railAmount);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(3), selectionData);
    },
  },
];

export const KLLT_UNIT_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.System,
  productCode: KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.unit.name",
  },
  apiCalls: {
    update: sendLinearUpdate,
    reset: sendLinearResetConfigurator,
    stepChange: sendLinearStepChange,
    stepReset: sendLinearDataReset,
    miscChange: sendLinearMiscChange,
    addToCart: sendLinearAddToCart,
  },
  summary: SummaryData,
  summaryParams: LinearSummaryParams,
  isAddToCartEnabled: (state: RootState) => {
    const kmats = state.linearConfigurator.kmats;

    const conflicts = state.linearConfigurator.conflicts;
    const validations = [0, 1, 2].map(
      (step) =>
        state.linearConfigurator.steps[step].substeps?.every(
          (substep) =>
            validateFromDefinition(state.linearConfigurator.substeps[substep] as LinearSubstep, kmats, conflicts)
              .substepValid,
        ) ?? false,
    );
    const addToCartEnabled = validations.every((validation) => validation);
    return addToCartEnabled;
  },
  isSaveEnabled: (state) => {
    return KLLT_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  canQueryAvailability: (state) => {
    return KLLT_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  canQueryPrice: (state) => {
    return KLLT_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  stepHardcodedData: UNIT_CONFIGURATOR_STEP_DATA,
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [materialName, sizeCode, version] = getCsticsValue(
      kmat,
      UnitCstic.DesignType_Series,
      UnitCstic.DesignType_SizeCode,
      UnitCstic.DesignType_Version,
    );

    const base = `${materialName || ""}${sizeCode || ""}`;
    const designation = version ? `${base}-${version}` : base;

    return designation || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kllt-system",
  modelAndUpdatePreprocessor: (model) => {
    const mainKmat = getMainKmat({ kmats: model.kmats });
    const namingHelper = new CsticNamingHelper(mainKmat.cstics);

    namingHelper.updateNames(UnitCstic.RailLocatingFaceSegment1, {
      label: "Guideway set 1 - guideway (T)",
      value: {
        label: (value) => `configurator.unit.cstic.railLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstic.RailLocatingFaceSegment2, {
      label: "Guideway set 2 - guideway (T)",
      value: {
        label: (value) => `configurator.unit.cstic.railLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstic.CarriageLocatingFaceSegment1, {
      label: "Guideway set 1 - carriage (T)",
      value: {
        label: (value) => `configurator.unit.cstic.carriageLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstic.CarriageLocatingFaceSegment2, {
      label: "Guideway set 2 - carriage (T)",
      value: {
        label: (value) => `configurator.unit.cstic.carriageLocatingFace.${value}.name`,
      },
    });

    const railKmats = model.kmats[KmatType.SubRail];
    railKmats.forEach(({ cstics }) => {
      const namingHelper = new CsticNamingHelper(cstics);

      namingHelper.updateNames(UnitCstic.AL, {
        label: "configurator.unit.section.al.name",
      });

      namingHelper.updateNames(UnitCstic.AR, {
        label: "configurator.unit.section.ar.name",
      });

      namingHelper.updateNames(UnitCstic.PartSizing, {
        label: "configurator.unit.cstic.partSizing.name",
        value: {
          label: (value) => `configurator.unit.cstic.partSizing.${value}.name`,
        },
      });

      namingHelper.updateNames(UnitCstic.PartAmount, {
        label: "configurator.unit.cstic.partAmount.name",
        value: {
          label: (value) => `configurator.unit.cstic.partAmount.${value}.name`,
        },
      });
    });

    const carriageKmats = model.kmats[KmatType.SubCarriage];
    carriageKmats.forEach(({ cstics }) => {
      const namingHelper = new CsticNamingHelper(cstics);

      namingHelper.updateNames(CarriageCstics.CarriageDesign, {
        label: "configurator.unit.step.referenceCarriage.name",
        value: {
          label: (value) => `configurator.unit.cstic.carriageType.${value}.name`,
        },
      });

      namingHelper.updateNames(CarriageCstics.KitsLeft, {
        label: "configurator.unit.cstic.kit.left.name",
        info: "configurator.unit.cstic.kit.left.info.KLLT",
      });

      namingHelper.updateNames(CarriageCstics.KitsCenter, {
        label: "configurator.unit.cstic.kit.center.name",
        info: "configurator.unit.cstic.kit.center.info.KLLT",
      });

      namingHelper.updateNames(CarriageCstics.KitsRight, {
        label: "configurator.unit.cstic.kit.right.name",
        info: "configurator.unit.cstic.kit.left.info.KLLT",
      });

      namingHelper.updateNames(CarriageCstics.LubricationConnector, {
        label: "configurator.unit.cstic.lubricationConnector.name",
        value: {
          imageUrl: (value) => `configurator.unit.cstic.lubricationConnector.${value}.image`,
        },
      });

      namingHelper.updateNames(CarriageCstics.LubricationConnectorOrientation, {
        // label: "configurator.unit.cstic.lubricationConnectorOrientation.name",
        // Deliberate change - Because both rows have the same name in summary
        label: "configurator.unit.cstic.lubricationConnector.name",
        value: {
          label: (value) => `configurator.unit.cstic.lubricationConnectorStatus.${value}.name`,
        },
      });

      const mapping: Record<string, string> = {
        [MANUAL_CONNECTORS_VALUE]: "configurator.unit.cstic.configureCarriage.section.lubricationConnector.manual.name",
        [CENTRAL_CONNECTORS_VALUE]:
          "configurator.unit.cstic.configureCarriage.section.lubricationConnector.central.name",
      };
      namingHelper.updateNames(CarriageCstics.LubricationConnectorType, {
        value: {
          label: (value) => mapping[value],
        },
      });
    });

    return model;
  },
  postDefinitionProcessor: (model) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
} as const;

type NamingProps = {
  label?: string;
  value?: {
    label?: (value: string) => string;
    imageUrl?: (value: string) => string;
  };
  info?: string;
};
class CsticNamingHelper {
  constructor(private cstics: Record<string, LinearCstic>) {}

  updateNames(csticName: string, { label, value, info }: NamingProps) {
    const cstic = this.cstics[csticName];
    if (!cstic) {
      return;
    }
    if (label) {
      cstic.label = label;
    }
    if (value) {
      cstic.values.forEach((val) => {
        if (value.label) {
          val.label = value.label(val.value);
        }
        if (value.imageUrl) {
          val.imageUrl = value.imageUrl(val.value);
        }
      });
    }
    if (info) {
      cstic.info = info;
    }
  }
}

function pageIsConfigured(model: LinearConfiguration, page: number): boolean {
  return (model.steps[page]!.substeps ?? []).every((substepType) => {
    const substep = model.substeps[substepType];

    substep.selectedTile = findSelectedTileIndex(substep as LinearSubstep, model.kmats);

    return validateFromDefinition(
      substep as LinearSubstep,
      model.kmats,
      (model as LinearConfiguration).conflictedCstics ?? [],
    ).substepValid;
  });
}

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const canVisitStep2 = pageIsConfigured(model, 0);
  const canVisitStep3 = pageIsConfigured(model, 1) && canVisitStep2;
  const canVisitStep4 = pageIsConfigured(model, 2) && canVisitStep3;

  const allowedSteps = [
    [true, canVisitStep2, canVisitStep3, canVisitStep4],
    [true, true, canVisitStep3, canVisitStep4],
    [true, true, true, canVisitStep4],
    [true, true, true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(KLLT_UNIT_DEFINITION);
